/**
 * React Query hooks for managing Deck related data.
 * These hooks provide clean interfaces for fetching, caching, and managing deck API data using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  ChangesInput,
  Deck,
  GetDeckQueryVariables,
  LockDeckMutationVariables,
  LockEvent,
  PublishDeckInput,
  PublishDeckMutationVariables,
  SaveDeckInput,
  SaveDeckMutationVariables,
} from '@services/api/deckManagement/graphql/schema/API';
import {
  lockDeck,
  publishDeck,
  releaseDeckLock,
  saveDeck,
} from '@services/api/deckManagement/graphql/schema/mutations';
import { getDeck } from '@services/api/deckManagement/graphql/schema/queries';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import {
  createQueryOrMutationKey,
  createStandardMutationOptions,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';
import { omit } from 'lodash';

import { decksQueryKeys } from './useAllDecks';

/**
 * Interface for common deck API parameters
 * @interface CommonDeckApiParams
 * @property {string} deckId - The unique identifier for the deck
 * @property {StandardQueryOptions | StandardMutationOptions} options - Additional React Query options
 */
export interface CommonDeckApiParams extends Omit<GetDeckQueryVariables, 'deck_id'> {
  deckId?: string;
  options?: StandardQueryOptions | StandardMutationOptions;
}

export const deckQueryKeys = {
  all: createQueryOrMutationKey('deck'),
  deck: (deckId: string) => createQueryOrMutationKey(...deckQueryKeys.all, deckId),
};

// Mutation keys for Deck modifications
export const deckMutationKeys = {
  all: createQueryOrMutationKey('deck'),
  save: () => createQueryOrMutationKey(...deckMutationKeys.all, 'save'),
  publish: () => createQueryOrMutationKey(...deckMutationKeys.all, 'publish'),
  lockDeck: () => createQueryOrMutationKey(...deckMutationKeys.all, 'lockDeck'),
  releaseDeck: () => createQueryOrMutationKey(...deckMutationKeys.all, 'releaseDeck'),
};

/**
 * Hook to fetch a single deck by ID
 * @param deckId - The unique identifier for the deck
 * @param options - Additional React Query options
 */
export const useDeck = ({ deckId, options }: CommonDeckApiParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: deckQueryKeys.deck(deckId || ''),
    queryFn: async (): Promise<Deck> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      // Call the GraphQL API using the imported query
      const response = await deckManagementClient.graphql({
        query: getDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getDeck) {
        return response.data.getDeck as Deck;
      }

      throw new Error('Failed to fetch deck or deck not found');
    },
    ...createStandardQueryOptions<Deck>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(deckId) && ((options as StandardQueryOptions)?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getDeck),
    }),
  });
};

/**
 * Interface for SaveDeck operation parameters
 * Extends CommonDeckApiParams and includes SaveDeckInput properties without save_timestamp
 * @param deckId - The unique identifier for the deck (as part of CommonDeckApiParams)
 * @param deckName - The name of the deck
 * @param change_set - The change set for the deck as ChangesInput
 * @param currentUser - The current user for lock validation
 * @param successCallback - Optional callback to execute on successful save
 * @interface
 */
export interface UseSaveDeckParams
  extends Omit<CommonDeckApiParams, 'options'>, Omit<SaveDeckInput, 'deck_id' | 'deck_name' | 'save_timestamp'> {
  deckName?: string;
  change_set: ChangesInput;
  currentUser: string;
  wasDeckLocked?: boolean; // Will be set internally during lock validation
  successCallback?: () => void;
}

/**
 * Hook for Saving Deck changes with added callback patterns
 * Current Callback Patterns (mutually exclusive - choose one):
 *
 * 1. Hook-level callbacks (for consistent behavior across all calls):
 *    const saveDeck = useSaveDeck({
 *      onSuccess: () => { // runs for all mutations }
 *    });
 *    saveDeck.mutate(params);
 *
 * 2. Per-call mutate callbacks (for call-specific behavior):
 *    const saveDeck = useSaveDeck();
 *    saveDeck.mutate(params, {
 *      onSuccess: () => { // runs for this call only }
 *    });
 *
 * 3. Params-based callbacks (recommended new use):
 *    const saveDeck = useSaveDeck();
 *    saveDeck.mutate({
 *      ...params,
 *      successCallback: () => { // runs for this call only }
 *    });
 *
 * WARNING: 1) Do NOT mix patterns 2 and 3 - per-call onSuccess overrides
 * the hook's onSuccess, preventing successCallback from executing.
 * 2) Per-call onSuccess (pattern 2) will completely override patterns 1 and 3.
 * Only use per-call callbacks if you need to completely replace the success handling.
 *
 * PRIORITY ORDER: Per-call callbacks (2) > Params callbacks (3) > Hook-level callbacks (1)*
 *
 * @param options - Additional Hook-Level mutation options (number 1 above)
 * @returns UseMutationResult for Saving a Deck
 */
export const useSaveDeck = (
  options?: StandardMutationOptions<boolean, Error, UseSaveDeckParams>,
): UseMutationResult<boolean, Error, UseSaveDeckParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: deckMutationKeys.save(),
    mutationFn: async (params: UseSaveDeckParams): Promise<boolean> => {
      const { deckId, deckName, change_set: changeSet, currentUser } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      if (!deckName) {
        throw new Error('Deck Name is required');
      }

      if (!currentUser) {
        throw new Error('Current user is required for lock validation');
      }

      // Check current deck lock status before saving to prevent race conditions
      const currentDeckResponse = await deckManagementClient.graphql({
        query: getDeck,
        variables: { deck_id: deckId },
      });

      if ('data' in currentDeckResponse && currentDeckResponse.data?.getDeck) {
        const currentDeck = currentDeckResponse.data.getDeck as Deck;

        // Check if deck is locked.
        if (currentDeck.locked_by && currentDeck.locked_by !== currentUser) {
          // If deck is locked by someone else, prevent save operation
          throw new Error(`Cannot save: Deck is locked by ${currentDeck.locked_by}`);
        }
      }

      const saveDeckInput: SaveDeckInput = {
        deck_id: deckId,
        deck_name: deckName,
        save_timestamp: new Date().toISOString(), // Matches expected AWSDateTime
        change_set: changeSet || null,
      };

      // Call the GraphQL API using the imported mutation
      const saveDeckResult = await deckManagementClient.graphql({
        query: saveDeck,
        variables: {
          request: saveDeckInput,
        } as SaveDeckMutationVariables,
      });

      if ('data' in saveDeckResult && saveDeckResult.data?.saveDeck) {
        return saveDeckResult.data.saveDeck;
      }

      throw new Error('Failed to SAVE deck');
    },
    // Create the other mutation options excluding onSuccess since it has an override below to accommodate a custom callback
    ...createStandardMutationOptions<boolean, Error, UseSaveDeckParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(saveDeck),
    }),
    onSuccess(data, variables) {
      // Invalidate all cached queries when save occurs.
      queryClient.invalidateQueries().then(() => {
        const { successCallback: mutateSuccessCallback } = variables;
        // Note*: These cannot co-exist, only one will run if both are defined (see the JSDoc)

        // Call hook-level onSuccess if defined
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }

        // Param-level onSuccess handling if defined
        if (mutateSuccessCallback) mutateSuccessCallback();
      });
    },
  });
};

/**
 * Interface for PublishDeck operation parameters
 * @param deckId - The unique identifier for the deck
 * @param publishId - The unique identifier for the publish operation
 * @param scheduleType - The schedule type for the publish operation
 * @param month - The month for the publish operation
 * @param successCallback - Optional callback to execute on successful publish
 * @interface
 */
export interface UsePublishDeckParams
  extends Omit<CommonDeckApiParams, 'options'>, Omit<PublishDeckInput, 'deck_id' | 'publish_timestamp'> {
  successCallback?: () => void;
}

/**
 * Hook for Publishing Deck with added callback patterns
 *
 * @param options - Additional Hook-Level mutation options (number 1 above)
 * @returns UseMutationResult for Publishing a Deck
 */
export const usePublishDeck = (
  options?: StandardMutationOptions<boolean, Error, UsePublishDeckParams>,
): UseMutationResult<boolean, Error, UsePublishDeckParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: deckMutationKeys.publish(),
    mutationFn: async (params: UsePublishDeckParams): Promise<boolean> => {
      const {
        deckId,
        publish_id: publishId,
        schedule_type: scheduleType,
        month,
        deck_cycle: deckCycle,
        publish_iteration_type: publishIterationType,
      } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      if (!publishId) {
        throw new Error('Publish ID is required');
      }

      const publishDeckInput: PublishDeckInput = {
        deck_id: deckId,
        publish_id: publishId,
        publish_timestamp: new Date().toISOString(),
        schedule_type: scheduleType,
        month,
        deck_cycle: deckCycle,
        publish_iteration_type: publishIterationType,
      };

      const publishDeckResult = await deckManagementClient.graphql({
        query: publishDeck,
        variables: {
          request: publishDeckInput,
        } as PublishDeckMutationVariables,
      });

      if ('data' in publishDeckResult && publishDeckResult.data?.publishDeck) {
        return publishDeckResult.data.publishDeck;
      }

      throw new Error('Failed to PUBLISH deck');
    },
    ...createStandardMutationOptions<boolean, Error, UsePublishDeckParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(publishDeck),
    }),
    onSuccess(data, variables) {
      queryClient.invalidateQueries().then(() => {
        const { successCallback: mutateSuccessCallback } = variables;

        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }

        if (mutateSuccessCallback) mutateSuccessCallback();
      });
    },
  });
};

/**
 * Interface for DeckLockout (i.e., LockDeck and ReleaseDeck) operation parameters
 * @param deckId - The unique identifier for the deck
 * @param successCallback - Optional callback to execute on successful lock/release
 * @interface
 */
export interface UseLockDeckParams {
  deckId: string;
  successCallback?: () => void;
}

/**
 * Hook for Locking Deck to support Multi-User lockout feature
 * @param options - Additional Hook-Level mutation options
 * @returns UseMutationResult for Locking a Deck
 */
export const useLockDeck = (
  options?: StandardMutationOptions<LockEvent, Error, UseLockDeckParams>,
): UseMutationResult<LockEvent, Error, UseLockDeckParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: deckMutationKeys.lockDeck(),
    mutationFn: async (params: UseLockDeckParams): Promise<LockEvent> => {
      const { deckId } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const lockDeckInput: LockDeckMutationVariables = {
        deck_id: deckId,
      };

      const lockDeckResult = await deckManagementClient.graphql({
        query: lockDeck,
        variables: lockDeckInput,
      });

      if ('data' in lockDeckResult && lockDeckResult.data?.lockDeck) {
        return lockDeckResult.data.lockDeck as LockEvent;
      }

      throw new Error('Failed to LOCK deck');
    },
    ...createStandardMutationOptions<LockEvent, Error, UseLockDeckParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(lockDeck),
    }),
    onSuccess(data, variables) {
      const { deckId, successCallback: mutateSuccessCallback } = variables;

      // Invalidate both individual deck and all decks list to refresh lock status
      queryClient.invalidateQueries({
        queryKey: deckQueryKeys.deck(deckId),
      });
      queryClient.invalidateQueries({
        queryKey: decksQueryKeys.list(),
      });

      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      }

      if (mutateSuccessCallback) mutateSuccessCallback();
    },
  });
};

/**
 * Hook for Releasing Deck Lock to support Multi-User lockout feature
 * @param options - Additional Hook-Level mutation options
 * @returns UseMutationResult for Releasing a locked Deck
 */
export const useReleaseDeck = (
  options?: StandardMutationOptions<LockEvent, Error, UseLockDeckParams>,
): UseMutationResult<LockEvent, Error, UseLockDeckParams> => {
  const queryClient = useQueryClient();
  const { deckManagementClient } = useApiClients();

  return useMutation({
    mutationKey: deckMutationKeys.releaseDeck(),
    mutationFn: async (params: UseLockDeckParams): Promise<LockEvent> => {
      const { deckId } = params;

      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const lockDeckInput: LockDeckMutationVariables = {
        deck_id: deckId,
      };

      const lockDeckResult = await deckManagementClient.graphql({
        query: releaseDeckLock,
        variables: lockDeckInput,
      });

      if ('data' in lockDeckResult && lockDeckResult.data?.releaseDeckLock) {
        return lockDeckResult.data.releaseDeckLock as LockEvent;
      }

      throw new Error('Failed to RELEASE deck');
    },
    ...createStandardMutationOptions<LockEvent, Error, UseLockDeckParams>({
      ...omit(options, 'onSuccess'),
      retryBypassExceptions: ['VALIDATION_ERROR', 'BATCH_OPERATION_ERROR'],
      rumOp: extractGraphqlOperationName(releaseDeckLock),
    }),
    onSuccess(data, variables) {
      const { deckId, successCallback: mutateSuccessCallback } = variables;

      // Invalidate both individual deck and all decks list to refresh lock status
      queryClient.invalidateQueries({
        queryKey: deckQueryKeys.deck(deckId),
      });
      queryClient.invalidateQueries({
        queryKey: decksQueryKeys.list(),
      });

      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      }

      if (mutateSuccessCallback) mutateSuccessCallback();
    },
  });
};
